import { HeaderConstants } from "@/constants";
import { Mutex } from "async-mutex";
import Cookies, { CookieSetOptions } from "universal-cookie";
import { v4 as uuidv4 } from "uuid";

export interface SessionHeaders {
  [key: string]: string | undefined;
}

export interface SessionCookieOptions extends CookieSetOptions {
  expires?: Date;
  path?: string;
  secure?: boolean;
  sameSite?: "strict" | "lax" | "none";
}

/**
 * Session Manager for unified logging strategy
 * Handles session ID generation, storage, and retrieval
 * Follows React Router best practices for session and cookie management:
 * https://reactrouter.com/explanation/sessions-and-cookies
 */
class SessionManager {
  private sessionId: string | null;
  private readonly cookieName: string;
  private readonly cookieExpiryDays: number;
  private readonly isSecure: boolean;
  private readonly _creationMutex: Mutex;
  private readonly cookies: Cookies;

  constructor() {
    this.sessionId = null;
    this.cookieName = "session_id";
    this.cookieExpiryDays = 30;
    this.isSecure = window.location.protocol === "https:";
    this._creationMutex = new Mutex();
    this.cookies = new Cookies();
  }

  generateSessionId(): string {
    return uuidv4();
  }

  getSessionId(): string | null {
    if (this.sessionId) {
      return this.sessionId;
    }

    const existingSessionId = this.getFromCookie();
    if (existingSessionId) {
      this.sessionId = existingSessionId;
      return this.sessionId;
    }

    return null;
  }

  /**
   * Safely creates and sets a new session ID if none exists.
   *
   * - Ensures that only one session creation runs at a time, even if multiple
   *   callers invoke this function concurrently.
   * - Returns the existing session ID if it's already set.
   * - Uses Mutex for concurrency control to ensure thread-safe session creation.
   *
   * @returns The existing or newly created session ID.
   */
  async createAndSetSessionId(): Promise<string> {
    const existingId = this.getSessionId();
    if (existingId) {
      return existingId;
    }

    return await this._creationMutex.runExclusive(async () => {
      // Double-check after acquiring mutex (another caller might have created it)
      const doubleCheckId = this.getSessionId();
      if (doubleCheckId) {
        return doubleCheckId;
      }

      const newSessionId = this.generateSessionId();
      this.setSessionId(newSessionId);
      return newSessionId;
    });
  }

  setSessionId(sessionId: string): void {
    if (!sessionId) {
      console.warn("[SessionManager] Cannot set null or undefined session ID");
      return;
    }
    this.sessionId = sessionId;
    this.setCookie(sessionId);
  }

  clearSessionId(): void {
    this.sessionId = null;
    this.clearCookie();
  }

  private getFromCookie(): string | null {
    try {
      const value = this.cookies.get(this.cookieName);
      return value || null;
    } catch (error) {
      console.warn("[SessionManager] Failed to get session ID from cookie:", error);
      return null;
    }
  }

  /**
   * Sets cookie following React Router's cookie best practices:
   * - Secure flag in production/HTTPS
   * - SameSite attribute for CSRF protection
   * - Proper path and expiration handling
   */
  private setCookie(sessionId: string): void {
    if (!sessionId) {
      console.warn("[SessionManager] Cannot set cookie with null or undefined session ID");
      return;
    }

    try {
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + this.cookieExpiryDays);

      const cookieOptions: SessionCookieOptions = {
        expires: expiryDate,
        path: "/",
        secure: this.isSecure,
        sameSite: "strict",
      };

      this.cookies.set(this.cookieName, sessionId, cookieOptions);
    } catch (error) {
      console.warn("[SessionManager] Failed to set session ID cookie:", error);
    }
  }

  private clearCookie(): void {
    try {
      const cookieOptions: SessionCookieOptions = {
        path: "/",
        secure: this.isSecure,
        sameSite: "strict",
      };

      this.cookies.remove(this.cookieName, cookieOptions);
    } catch (error) {
      console.warn("[SessionManager] Failed to clear session ID cookie:", error);
    }
  }

  getSessionHeaders(): SessionHeaders {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      return {};
    }
    return {
      [HeaderConstants.SESSION_ID_HEADER]: sessionId,
    };
  }
}

// Export singleton instance.
export const sessionManager = new SessionManager();
